import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { cn } from '../lib/utils';
import api from '../lib/api';
import {
  Loader2,
  Building2,
  User,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Mail,
  Lock,
  MapPin,
  Hash,
  Calculator,
  Users,
  Check,
  Package,
  Search,
  X,
  Shield,
  Globe,
} from 'lucide-react';

const APP_ICON_MAP = { 'nexus-compta': Calculator, 'nexus-sirh': Users };
const APP_COLOR_MAP = { 'nexus-compta': '#2563eb', 'nexus-sirh': '#10b981' };

/* ------------------------------------------------------------------ */
/*  Zod Schemas                                                       */
/* ------------------------------------------------------------------ */

const companySchema = z.object({
  nom: z.string().min(1, 'required'),
  siret: z.string().regex(/^\d{14}$/, 'siret_invalid'),
  adresse: z.string().min(1, 'required'),
  code_postal: z.string().min(1, 'required'),
  ville: z.string().min(1, 'required'),
  telephone: z.string().optional(),
  email: z.string().min(1, 'required').email('email_invalid'),
});

const accountSchema = z
  .object({
    prenom: z.string().min(1, 'required'),
    nom: z.string().min(1, 'required'),
    email: z.string().min(1, 'required').email('email_invalid'),
    password: z.string().min(8, 'password_min'),
    password_confirm: z.string().min(1, 'required'),
    cgu: z.literal(true, { errorMap: () => ({ message: 'cgu_required' }) }),
  })
  .refine((d) => d.password === d.password_confirm, {
    message: 'passwords_mismatch',
    path: ['password_confirm'],
  });

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function getPasswordStrength(pw) {
  if (!pw || pw.length < 8) return 'weak';
  const hasUpper = /[A-Z]/.test(pw);
  const hasNumber = /\d/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  if (pw.length >= 12 || (hasUpper && hasNumber && hasSpecial)) return 'strong';
  if (pw.length >= 8) return 'medium';
  return 'weak';
}

const strengthColors = { weak: 'bg-red-500', medium: 'bg-orange-400', strong: 'bg-green-500' };
const strengthWidths = { weak: 'w-1/3', medium: 'w-2/3', strong: 'w-full' };

/* ------------------------------------------------------------------ */
/*  InputField                                                         */
/* ------------------------------------------------------------------ */

function InputField({ id, label, icon: Icon, type = 'text', error, registration, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          id={id}
          type={type}
          {...registration}
          className={cn(
            'w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            error ? 'border-red-300 bg-red-50' : 'border-slate-300 bg-white'
          )}
          placeholder={placeholder}
        />
      </div>
      {error && <p className="mt-1.5 text-sm text-red-600">{error.message}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Progress Indicator                                                 */
/* ------------------------------------------------------------------ */

function ProgressIndicator({ step, t }) {
  const steps = [
    t('register.progress.step1'),
    t('register.progress.step2'),
    t('register.progress.step3'),
    t('register.progress.step4'),
  ];

  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((label, i) => {
        const isCompleted = i < step;
        const isCurrent = i === step;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <div
                className={cn(
                  'h-0.5 w-8 sm:w-12 transition-colors',
                  i <= step ? 'bg-blue-500' : 'bg-slate-200'
                )}
              />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors border-2',
                  isCompleted && 'bg-green-500 border-green-500 text-white',
                  isCurrent && 'bg-blue-600 border-blue-600 text-white',
                  !isCompleted && !isCurrent && 'bg-white border-slate-300 text-slate-400'
                )}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span
                className={cn(
                  'text-xs font-medium hidden sm:block',
                  isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-slate-400'
                )}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ================================================================== */
/*  REGISTER COMPONENT                                                */
/* ================================================================== */

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register: authRegister } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');

  // Step 0 — Company
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const searchTimer = useRef(null);

  // Step 1 — Apps / Plans
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [selectedApps, setSelectedApps] = useState({});
  // selectedApps shape: { 'nexus-compta': 'pro', 'nexus-sirh': 'starter' }

  // Step data saved across steps
  const [companyData, setCompanyData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /* ------- Company form (step 0) ------- */
  const companyForm = useForm({
    resolver: zodResolver(companySchema),
    defaultValues: {
      nom: '', siret: '', adresse: '', code_postal: '', ville: '', telephone: '', email: '',
    },
  });

  /* ------- Account form (step 2) ------- */
  const accountForm = useForm({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      prenom: '', nom: '', email: '', password: '', password_confirm: '', cgu: false,
    },
  });

  const watchPassword = accountForm.watch('password');
  const pwStrength = getPasswordStrength(watchPassword);

  /* ------- Fetch pricing on mount ------- */
  useEffect(() => {
    api
      .get('/api/apps/pricing')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data.apps || [];
        setApps(list);

        // Pre-select from URL params
        const preApp = searchParams.get('app');
        const prePlan = searchParams.get('plan');
        if (preApp) {
          const matchedApp = list.find((a) => a.code === preApp);
          if (matchedApp) {
            const plans = matchedApp.plans || [];
            const matchedPlan = prePlan
              ? plans.find((p) => p.code === prePlan)
              : plans[0];
            setSelectedApps({ [preApp]: matchedPlan?.code || plans[0]?.code || '' });
          }
        }
      })
      .catch(() => setApps([]))
      .finally(() => setAppsLoading(false));
  }, [searchParams]);

  /* ------- Company search (debounced) ------- */
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(() => {
      setSearching(true);
      api
        .get('/api/auth/search-company?q=' + encodeURIComponent(term))
        .then((res) => {
          setSearchResults(Array.isArray(res.data) ? res.data : res.data.results || []);
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
  }, []);

  const selectCompanyResult = (result) => {
    companyForm.setValue('nom', result.nom || '');
    companyForm.setValue('siret', result.siret || '');
    companyForm.setValue('adresse', result.adresse || '');
    companyForm.setValue('code_postal', result.code_postal || '');
    companyForm.setValue('ville', result.ville || '');
    if (result.email) companyForm.setValue('email', result.email);
    if (result.telephone) companyForm.setValue('telephone', result.telephone);
    setSearchTerm('');
    setSearchResults([]);
    setShowManual(true);
  };

  const switchToManual = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowManual(true);
  };

  /* ------- App toggle ------- */
  const toggleApp = (appCode) => {
    setSelectedApps((prev) => {
      const copy = { ...prev };
      if (copy[appCode]) {
        delete copy[appCode];
      } else {
        const app = apps.find((a) => a.code === appCode);
        const plans = app?.plans || [];
        copy[appCode] = plans[0]?.code || '';
      }
      return copy;
    });
  };

  const selectPlan = (appCode, planCode) => {
    setSelectedApps((prev) => ({ ...prev, [appCode]: planCode }));
  };

  /* ------- Step navigation ------- */
  const onCompanySubmit = (data) => {
    setCompanyData(data);
    setStep(1);
  };

  const onAppsNext = () => {
    if (Object.keys(selectedApps).length === 0) return;
    setStep(2);
  };

  const onAccountSubmit = (data) => {
    setAccountData(data);
    setStep(3);
  };

  const onFinalSubmit = async () => {
    setServerError('');
    setSubmitting(true);
    const payload = {
      structure: {
        nom: companyData.nom,
        siret: companyData.siret,
        adresse: companyData.adresse,
        code_postal: companyData.code_postal,
        ville: companyData.ville,
        telephone: companyData.telephone || '',
        email: companyData.email,
      },
      user: {
        prenom: accountData.prenom,
        nom: accountData.nom,
        email: accountData.email,
        password: accountData.password,
      },
      selected_apps: Object.entries(selectedApps).map(([app_code, plan_code]) => ({
        app_code,
        plan_code,
      })),
    };
    const result = await authRegister(payload);
    setSubmitting(false);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setServerError(result.error);
    }
  };

  /* ------- Helpers for summary ------- */
  const getPlanForApp = (appCode) => {
    const app = apps.find((a) => a.code === appCode);
    const planCode = selectedApps[appCode];
    return app?.plans?.find((p) => p.code === planCode) || null;
  };

  const totalPrice = Object.keys(selectedApps).reduce((sum, appCode) => {
    const plan = getPlanForApp(appCode);
    return sum + (plan?.price_monthly_ht || 0);
  }, 0);

  /* ------- Translate error code ------- */
  const te = (code) => {
    if (!code) return '';
    const key = `register.errors.${code}`;
    const translated = t(key);
    return translated !== key ? translated : code;
  };

  /* ================================================================ */
  /*  RENDER                                                          */
  /* ================================================================ */

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-8">
            <span className="text-white font-bold text-3xl">N</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Nexus-360</h1>
          <p className="text-xl text-blue-200 leading-relaxed max-w-md">
            {t('landing.hero.subtitle')}
          </p>
          <div className="mt-8 flex flex-col gap-3 text-blue-200 text-sm">
            <div className="flex items-center gap-2"><Check className="w-4 h-4" /> {t('common.free_trial')}</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4" /> {t('common.no_credit_card')}</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4" /> {t('common.no_commitment')}</div>
          </div>
        </div>
      </div>

      {/* Right panel — wizard */}
      <div className="flex-1 flex items-start justify-center px-4 sm:px-6 py-8 bg-slate-50 overflow-y-auto">
        <div className="w-full max-w-2xl">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-bold text-2xl">N</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Nexus-360</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
            <ProgressIndicator step={step} t={t} />

            {serverError && (
              <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                {serverError}
              </div>
            )}

            {/* ============================================ */}
            {/*  STEP 0 — Company                            */}
            {/* ============================================ */}
            {step === 0 && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">{t('register.step1.title')}</h2>
                  <p className="text-slate-500 mt-2">{t('register.step1.subtitle')}</p>
                </div>

                {/* Search bar */}
                {!showManual && (
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder={t('register.step1.search_placeholder')}
                        className="w-full pl-10 pr-10 py-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => { setSearchTerm(''); setSearchResults([]); }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Search results dropdown */}
                    {(searching || searchResults.length > 0) && searchTerm && (
                      <div className="mt-1 border border-slate-200 rounded-lg bg-white shadow-lg max-h-60 overflow-y-auto">
                        {searching && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                          </div>
                        )}
                        {!searching && searchResults.length === 0 && (
                          <p className="py-4 px-4 text-sm text-slate-500 text-center">
                            {t('register.step1.search_no_results')}
                          </p>
                        )}
                        {!searching &&
                          searchResults.map((r, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => selectCompanyResult(r)}
                              className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                            >
                              <p className="text-sm font-semibold text-slate-900">{r.nom}</p>
                              <p className="text-xs text-slate-500">
                                SIRET: {r.siret} — {r.ville}
                              </p>
                            </button>
                          ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={switchToManual}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {t('register.step1.manual_link')}
                    </button>
                  </div>
                )}

                {/* Company form */}
                {showManual && (
                  <form onSubmit={companyForm.handleSubmit(onCompanySubmit)} className="space-y-4">
                    <InputField
                      id="company_nom"
                      label={t('register.step1.fields.nom')}
                      icon={Building2}
                      error={companyForm.formState.errors.nom}
                      registration={companyForm.register('nom')}
                      placeholder="Ma Societe SAS"
                    />
                    <InputField
                      id="company_siret"
                      label={t('register.step1.fields.siret')}
                      icon={Hash}
                      error={companyForm.formState.errors.siret}
                      registration={companyForm.register('siret')}
                      placeholder="12345678901234"
                    />
                    <InputField
                      id="company_adresse"
                      label={t('register.step1.fields.adresse')}
                      icon={MapPin}
                      error={companyForm.formState.errors.adresse}
                      registration={companyForm.register('adresse')}
                      placeholder="12 Rue de la Paix"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        id="company_code_postal"
                        label={t('register.step1.fields.code_postal')}
                        icon={MapPin}
                        error={companyForm.formState.errors.code_postal}
                        registration={companyForm.register('code_postal')}
                        placeholder="75001"
                      />
                      <InputField
                        id="company_ville"
                        label={t('register.step1.fields.ville')}
                        icon={MapPin}
                        error={companyForm.formState.errors.ville}
                        registration={companyForm.register('ville')}
                        placeholder="Paris"
                      />
                    </div>
                    <InputField
                      id="company_telephone"
                      label={t('register.step1.fields.telephone')}
                      icon={Globe}
                      error={companyForm.formState.errors.telephone}
                      registration={companyForm.register('telephone')}
                      placeholder="01 23 45 67 89"
                    />
                    <InputField
                      id="company_email"
                      label={t('register.step1.fields.email')}
                      icon={Mail}
                      type="email"
                      error={companyForm.formState.errors.email}
                      registration={companyForm.register('email')}
                      placeholder="contact@masociete.fr"
                    />

                    <div className="flex justify-end mt-6">
                      <button
                        type="submit"
                        className="flex items-center justify-center gap-2 py-2.5 px-6 rounded-lg bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                      >
                        {t('common.next')}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* ============================================ */}
            {/*  STEP 1 — Applications                       */}
            {/* ============================================ */}
            {step === 1 && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">{t('register.step2.title')}</h2>
                  <p className="text-slate-500 mt-2">{t('register.step2.subtitle')}</p>
                </div>

                {appsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {apps.map((app) => {
                        const Icon = APP_ICON_MAP[app.code] || Package;
                        const color = APP_COLOR_MAP[app.code] || '#2563eb';
                        const isSelected = !!selectedApps[app.code];
                        const plans = app.plans || [];
                        const selectedPlanCode = selectedApps[app.code];

                        return (
                          <div key={app.code}>
                            {/* App card */}
                            <button
                              type="button"
                              onClick={() => toggleApp(app.code)}
                              className={cn(
                                'relative w-full text-left p-5 rounded-xl border-2 transition-all duration-200',
                                isSelected
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                              )}
                            >
                              <div className="flex items-start gap-4">
                                <div
                                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                                  style={{ background: `${color}15` }}
                                >
                                  <Icon className="w-6 h-6" style={{ color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h3 className="text-base font-bold text-slate-900">{app.nom}</h3>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      {t('common.free_trial')}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-500 mt-0.5">{app.description}</p>
                                </div>
                                <div
                                  className={cn(
                                    'w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-colors',
                                    isSelected
                                      ? 'border-blue-600 bg-blue-600'
                                      : 'border-slate-300 bg-white'
                                  )}
                                >
                                  {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                </div>
                              </div>
                            </button>

                            {/* Plan cards below selected app */}
                            {isSelected && plans.length > 0 && (
                              <div className="mt-3 ml-4 mr-4">
                                <p className="text-sm font-medium text-slate-600 mb-2">
                                  {t('register.step2.select_plan')}
                                </p>
                                <div
                                  className="grid gap-3"
                                  style={{
                                    gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, minmax(0, 1fr))`,
                                  }}
                                >
                                  {plans.map((plan) => {
                                    const isPlanSelected = selectedPlanCode === plan.code;
                                    const isRecommended = plan.code === 'pro';
                                    return (
                                      <button
                                        key={plan.code}
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          selectPlan(app.code, plan.code);
                                        }}
                                        className={cn(
                                          'relative text-left p-4 rounded-lg border-2 transition-all',
                                          isPlanSelected
                                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                                            : 'border-slate-200 bg-white hover:border-slate-300'
                                        )}
                                      >
                                        {isRecommended && (
                                          <span className="absolute -top-2.5 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                                            {t('register.step2.recommended')}
                                          </span>
                                        )}
                                        <div className="flex items-center gap-2 mb-2">
                                          <div
                                            className={cn(
                                              'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                                              isPlanSelected
                                                ? 'border-blue-600'
                                                : 'border-slate-300'
                                            )}
                                          >
                                            {isPlanSelected && (
                                              <div className="w-2 h-2 rounded-full bg-blue-600" />
                                            )}
                                          </div>
                                          <span className="text-sm font-semibold text-slate-900">
                                            {plan.nom || plan.name}
                                          </span>
                                        </div>
                                        <p className="text-lg font-bold text-slate-900 mb-2">
                                          {plan.price_monthly_ht}€{' '}
                                          <span className="text-sm font-normal text-slate-500">
                                            {t('common.ht_month')}
                                          </span>
                                        </p>
                                        {plan.modules && plan.modules.length > 0 && (
                                          <ul className="space-y-1">
                                            {plan.modules.map((mod, mi) => (
                                              <li
                                                key={mi}
                                                className="flex items-center gap-1.5 text-xs text-slate-600"
                                              >
                                                <Check className="w-3 h-3 text-green-500 shrink-0" />
                                                {typeof mod === 'string' ? mod : mod.nom || mod.name}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <p className="mt-4 text-sm text-slate-500 text-center">
                      {t('register.step2.selected_count', { count: Object.keys(selectedApps).length })}
                    </p>

                    {Object.keys(selectedApps).length === 0 && (
                      <p className="mt-2 text-sm text-red-500 text-center">
                        {t('register.step2.validation_error')}
                      </p>
                    )}

                    <div className="flex gap-3 mt-6">
                      <button
                        type="button"
                        onClick={() => setStep(0)}
                        className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        {t('common.back')}
                      </button>
                      <button
                        type="button"
                        onClick={onAppsNext}
                        disabled={Object.keys(selectedApps).length === 0}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {t('common.next')}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ============================================ */}
            {/*  STEP 2 — Account                            */}
            {/* ============================================ */}
            {step === 2 && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">{t('register.step3.title')}</h2>
                  <p className="text-slate-500 mt-2">{t('register.step3.subtitle')}</p>
                </div>

                <form onSubmit={accountForm.handleSubmit(onAccountSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      id="account_prenom"
                      label={t('register.step3.fields.prenom')}
                      icon={User}
                      error={accountForm.formState.errors.prenom}
                      registration={accountForm.register('prenom')}
                      placeholder="Jean"
                    />
                    <InputField
                      id="account_nom"
                      label={t('register.step3.fields.nom')}
                      icon={User}
                      error={accountForm.formState.errors.nom}
                      registration={accountForm.register('nom')}
                      placeholder="Dupont"
                    />
                  </div>

                  <InputField
                    id="account_email"
                    label={t('register.step3.fields.email')}
                    icon={Mail}
                    type="email"
                    error={accountForm.formState.errors.email}
                    registration={accountForm.register('email')}
                    placeholder="jean.dupont@masociete.fr"
                  />

                  {/* Password with strength */}
                  <div>
                    <label htmlFor="account_password" className="block text-sm font-medium text-slate-700 mb-1.5">
                      {t('register.step3.fields.password')}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        id="account_password"
                        type={showPassword ? 'text' : 'password'}
                        {...accountForm.register('password')}
                        className={cn(
                          'w-full pl-10 pr-12 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                          accountForm.formState.errors.password
                            ? 'border-red-300 bg-red-50'
                            : 'border-slate-300 bg-white'
                        )}
                        placeholder="Minimum 8 caracteres"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {/* Strength indicator */}
                    {watchPassword && (
                      <div className="mt-2">
                        <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all duration-300',
                              strengthWidths[pwStrength],
                              strengthColors[pwStrength]
                            )}
                          />
                        </div>
                        <p
                          className={cn(
                            'text-xs mt-1',
                            pwStrength === 'weak' && 'text-red-500',
                            pwStrength === 'medium' && 'text-orange-500',
                            pwStrength === 'strong' && 'text-green-600'
                          )}
                        >
                          {t(`register.step3.password_strength.${pwStrength}`)}
                        </p>
                      </div>
                    )}
                    {accountForm.formState.errors.password && (
                      <p className="mt-1.5 text-sm text-red-600">
                        {te(accountForm.formState.errors.password.message)}
                      </p>
                    )}
                  </div>

                  {/* Password confirm */}
                  <div>
                    <label htmlFor="account_password_confirm" className="block text-sm font-medium text-slate-700 mb-1.5">
                      {t('register.step3.fields.password_confirm')}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        id="account_password_confirm"
                        type={showPassword ? 'text' : 'password'}
                        {...accountForm.register('password_confirm')}
                        className={cn(
                          'w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                          accountForm.formState.errors.password_confirm
                            ? 'border-red-300 bg-red-50'
                            : 'border-slate-300 bg-white'
                        )}
                        placeholder="Repetez le mot de passe"
                      />
                    </div>
                    {accountForm.formState.errors.password_confirm && (
                      <p className="mt-1.5 text-sm text-red-600">
                        {te(accountForm.formState.errors.password_confirm.message)}
                      </p>
                    )}
                  </div>

                  {/* CGU checkbox */}
                  <div className="pt-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        {...accountForm.register('cgu')}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-600">
                        {t('register.step3.cgu_accept')}{' '}
                        <Link to="/cgu" target="_blank" className="text-blue-600 hover:underline">
                          {t('register.step3.cgu_link')}
                        </Link>{' '}
                        {t('register.step3.and')}{' '}
                        <Link to="/confidentialite" target="_blank" className="text-blue-600 hover:underline">
                          {t('register.step3.privacy_link')}
                        </Link>
                      </span>
                    </label>
                    {accountForm.formState.errors.cgu && (
                      <p className="mt-1.5 text-sm text-red-600 ml-7">
                        {te(accountForm.formState.errors.cgu.message)}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      {t('common.back')}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      {t('common.next')}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ============================================ */}
            {/*  STEP 3 — Summary                            */}
            {/* ============================================ */}
            {step === 3 && (
              <div>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">{t('register.step4.title')}</h2>
                  <p className="text-slate-500 mt-2">{t('register.step4.subtitle')}</p>
                </div>

                <div className="space-y-5">
                  {/* Company section */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        {t('register.step4.section_company')}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setStep(0)}
                        className="text-xs text-blue-600 hover:text-blue-700 hover:underline font-medium"
                      >
                        {t('register.step4.modify')}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <p className="text-slate-500">{t('register.step1.fields.nom')}</p>
                      <p className="text-slate-900 font-medium">{companyData?.nom}</p>
                      <p className="text-slate-500">SIRET</p>
                      <p className="text-slate-900 font-medium">{companyData?.siret}</p>
                      <p className="text-slate-500">{t('register.step1.fields.ville')}</p>
                      <p className="text-slate-900 font-medium">{companyData?.ville}</p>
                    </div>
                  </div>

                  {/* Apps section */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-500" />
                        {t('register.step4.section_apps')}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="text-xs text-blue-600 hover:text-blue-700 hover:underline font-medium"
                      >
                        {t('register.step4.modify')}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(selectedApps).map(([appCode, planCode]) => {
                        const app = apps.find((a) => a.code === appCode);
                        const plan = getPlanForApp(appCode);
                        const AppIcon = APP_ICON_MAP[appCode] || Package;
                        return (
                          <div key={appCode} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <AppIcon className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-900 font-medium">{app?.nom || appCode}</span>
                              <span className="text-slate-400">-</span>
                              <span className="text-slate-600">{plan?.nom || plan?.name || planCode}</span>
                            </div>
                            <span className="text-slate-900 font-semibold">
                              {plan?.price_monthly_ht || 0}€ {t('common.ht_month')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Admin section */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-500" />
                        {t('register.step4.section_admin')}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="text-xs text-blue-600 hover:text-blue-700 hover:underline font-medium"
                      >
                        {t('register.step4.modify')}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <p className="text-slate-500">{t('register.step3.fields.prenom')}</p>
                      <p className="text-slate-900 font-medium">{accountData?.prenom}</p>
                      <p className="text-slate-500">{t('register.step3.fields.nom')}</p>
                      <p className="text-slate-900 font-medium">{accountData?.nom}</p>
                      <p className="text-slate-500">{t('register.step3.fields.email')}</p>
                      <p className="text-slate-900 font-medium">{accountData?.email}</p>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900">
                        {t('register.step4.total')}
                      </span>
                      <span className="text-lg font-bold text-slate-900">
                        {totalPrice}€ {t('common.ht_month')}
                      </span>
                    </div>
                  </div>

                  {/* Trial notice callout */}
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm text-green-800">{t('register.step4.trial_notice')}</p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <button
                    type="button"
                    onClick={onFinalSubmit}
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-green-600 text-white font-semibold text-base hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                    {submitting ? t('common.loading') : t('register.step4.submit')}
                  </button>
                  <p className="text-xs text-slate-400 text-center">
                    {t('register.step4.submit_notice')}
                  </p>
                </div>

                <div className="flex justify-start mt-4">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t('common.back')}
                  </button>
                </div>
              </div>
            )}

            {/* Login link */}
            <p className="mt-6 text-center text-sm text-slate-500">
              {t('common.or')}{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
                {t('nav.login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
